<template>
  <manual-page
    title="Tienda en Linea"
    description="Guia completa para administrar la tienda de productos adicionales, categorias y ordenes de compra."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'productsIndex' }"
    action-text="Ver productos"
  >
    <!-- Overview -->
    <h2>Vista General de la Tienda</h2>
    <p>
      La tienda en linea es un modulo complementario que permite vender productos
      adicionales a los clientes de Trunimal. Aqui puedes ofrecer snacks, suplementos,
      accesorios y otros articulos relacionados con el cuidado de mascotas.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">La tienda te permite:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Vender productos adicionales a clientes existentes</li>
        <li>Ofrecer snacks, premios y complementos alimenticios</li>
        <li>Gestionar un catalogo de accesorios para mascotas</li>
        <li>Generar ingresos complementarios al plan de alimentacion</li>
        <li>Facilitar compras directas desde la app del cliente</li>
      </ul>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Los productos de la tienda son independientes de los planes
        de alimentacion. Los clientes pueden comprarlos en cualquier momento sin afectar su plan.
      </p>
    </div>

    <!-- Products Section -->
    <h2>Gestion de Productos</h2>
    <p>
      El catalogo de productos es el corazon de la tienda. Aqui administras
      todos los articulos disponibles para la venta.
    </p>

    <h3>Lista de Productos</h3>
    <p>
      La lista principal muestra todos los productos registrados. Puedes filtrar
      por categoria, estado o buscar por nombre.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Informacion mostrada</h3>
          <p class="text-gray-600 mb-2">Cada producto muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Imagen:</strong> Foto principal del producto</li>
            <li><strong>Nombre:</strong> Identificador del producto</li>
            <li><strong>Precio:</strong> Precio de venta al publico</li>
            <li><strong>Categoria:</strong> Clasificacion del producto</li>
            <li><strong>Estado:</strong> Activo o inactivo</li>
            <li><strong>Stock:</strong> Unidades disponibles</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Creating Products -->
    <h3>Crear un Producto</h3>
    <p>Para agregar un nuevo producto al catalogo:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al formulario</h3>
          <p class="text-gray-600">
            Desde la lista de productos, haz clic en "Crear producto".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Informacion basica</h3>
          <p class="text-gray-600 mb-2">Completa los datos del producto:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre:</strong> Nombre descriptivo del producto</li>
            <li><strong>Descripcion:</strong> Detalle completo, beneficios e ingredientes</li>
            <li><strong>Precio:</strong> Precio de venta</li>
            <li><strong>Categoria:</strong> Selecciona la categoria correspondiente</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Subir imagenes</h3>
          <p class="text-gray-600">
            Agrega fotos del producto. Una buena imagen aumenta significativamente
            las ventas. Usa fotos de alta calidad con fondo limpio.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Configurar stock</h3>
          <p class="text-gray-600">
            Define la cantidad de unidades disponibles. El sistema avisara
            cuando el stock este bajo para que puedas reabastecer.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">5</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Guardar y publicar</h3>
          <p class="text-gray-600">
            Una vez completo, guarda el producto. Puedes dejarlo activo para
            que aparezca en la tienda o inactivo si aun no esta listo.
          </p>
        </div>
      </div>
    </div>

    <!-- Editing Products -->
    <h3>Editar un Producto</h3>
    <p>Para modificar un producto existente:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al detalle</h3>
          <p class="text-gray-600">
            Haz clic en el producto que deseas modificar desde la lista.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Realizar cambios</h3>
          <p class="text-gray-600 mb-2">Puedes modificar:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Nombre y descripcion</li>
            <li>Precio de venta</li>
            <li>Imagenes del producto</li>
            <li>Categoria asignada</li>
            <li>Stock disponible</li>
            <li>Estado (activar/desactivar)</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Los cambios de precio no afectan ordenes ya realizadas,
        solo aplican a compras futuras.
      </p>
    </div>

    <!-- Categories Section -->
    <h2>Categorias de Productos</h2>
    <p>
      Las categorias ayudan a organizar el catalogo y facilitan que los clientes
      encuentren lo que buscan.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Categorias tipicas:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Snacks y premios:</strong> Golosinas y recompensas para mascotas</li>
        <li><strong>Suplementos:</strong> Vitaminas, probioticos, complementos</li>
        <li><strong>Accesorios:</strong> Platos, juguetes, camas</li>
        <li><strong>Higiene:</strong> Shampoos, cepillos, productos de limpieza</li>
        <li><strong>Otros:</strong> Productos varios</li>
      </ul>
    </div>

    <h3>Crear una Categoria</h3>
    <p>Para agregar una nueva categoria:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder a categorias</h3>
          <p class="text-gray-600">
            Ve a Tienda > Categorias y haz clic en "Crear categoria".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Definir categoria</h3>
          <p class="text-gray-600">
            Ingresa el nombre de la categoria y una descripcion opcional.
            Usa nombres claros que los clientes entiendan facilmente.
          </p>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> No crees demasiadas categorias. Es mejor tener pocas
        categorias bien organizadas que muchas con pocos productos cada una.
      </p>
    </div>

    <!-- Product Orders Section -->
    <h2>Ordenes de Compra</h2>
    <p>
      Las ordenes de compra se generan cuando un cliente adquiere productos de la tienda.
      Son independientes de las ordenes de alimento y tienen su propio flujo.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Flujo de ordenes</h3>
          <p class="text-gray-600 mb-2">Las ordenes de compra pasan por estos estados:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Pendiente:</strong> Orden recibida, esperando pago</li>
            <li><strong>Pagada:</strong> Pago confirmado, lista para preparar</li>
            <li><strong>En preparacion:</strong> Empacando productos</li>
            <li><strong>Enviada:</strong> En camino al cliente</li>
            <li><strong>Entregada:</strong> Orden completada</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Gestionar ordenes</h3>
          <p class="text-gray-600">
            Desde la seccion de Ordenes de Compra puedes ver todas las ordenes,
            filtrar por estado, y actualizar el progreso de cada una.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Detalle de orden</h3>
          <p class="text-gray-600 mb-2">Cada orden muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Datos del cliente y direccion de entrega</li>
            <li>Lista de productos comprados con cantidades</li>
            <li>Total de la orden y metodo de pago</li>
            <li>Historial de cambios de estado</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Differences with Food Orders -->
    <h2>Diferencias con Ordenes de Alimento</h2>
    <p>
      Es importante entender que las ordenes de compra de la tienda son diferentes
      a las ordenes de los planes de alimentacion:
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Ordenes de compra (Tienda):</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Son compras unicas, no recurrentes</li>
        <li>El cliente las realiza cuando quiere</li>
        <li>Pueden incluir multiples productos</li>
        <li>Se gestionan de forma independiente</li>
        <li>No requieren un plan activo</li>
      </ul>
    </div>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Ordenes de alimento (Planes):</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Se generan automaticamente segun la frecuencia del plan</li>
        <li>Contienen alimento segun la dieta de la mascota</li>
        <li>Estan ligadas a un plan activo</li>
        <li>Se cobran de forma recurrente</li>
      </ul>
    </div>

    <!-- Best Practices -->
    <h2>Buenas Practicas</h2>
    <ul>
      <li><strong>Fotos de calidad:</strong> Invierte en buenas fotografias de productos</li>
      <li><strong>Descripciones completas:</strong> Incluye ingredientes, beneficios y modo de uso</li>
      <li><strong>Precios competitivos:</strong> Investiga el mercado antes de fijar precios</li>
      <li><strong>Stock actualizado:</strong> Mantén el inventario al dia para evitar vender productos agotados</li>
      <li><strong>Productos relacionados:</strong> Agrupa productos que se complementen</li>
    </ul>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Ofrece productos que complementen la dieta natural.
        Los clientes de Trunimal buscan opciones saludables para sus mascotas.
      </p>
    </div>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualStore",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualOrders", label: "Ordenes de alimento" },
        { route: "manualDietManagement", label: "Gestion de alimentos" },
        { route: "manualAccounting", label: "Contabilidad" },
        { route: "manualReports", label: "Reportes" },
      ],
    };
  },
};
</script>
